/**
 * Client for the WORKFLOW code-node debugger WebSocket (ADR-0067 S4).
 *
 * Drives `WS /api/admin/workflows/{workflowId}/nodes/{nodeId}/debug` — the flows-plane
 * twin of the App-builder `queryDebug.ts`. The server streams `stopped` / `terminated`
 * events; we send `setBreakpoints` / step / `vars` / `evaluate`. UNLIKE the App-builder
 * debugger, the CODE + sample INPUT ride in an INIT FRAME sent right after the socket
 * opens (the operator debugs the editor buffer, which can be large — a query param
 * would be ugly). The JWT rides as `?token=` (a WebSocket can't set an auth header).
 * Framework-agnostic + unit-testable via an injected `WebSocket` ctor.
 */

import { api } from "@/lib/api";

export type StepKind = "continue" | "stepOver" | "stepIn" | "stepOut";

/** The editor lines a breakpoint can bind to (QuickJS pc2line). `available` is false on
 *  an old debug engine → the UI leaves the gutter unconfined. */
export interface BreakpointLines {
  available: boolean;
  lines: number[];
}

/** POST the editor-buffer code and get its executable (breakpoint-bindable) lines. */
export function fetchCodeNodeBreakpointLines(
  workflowId: string,
  nodeId: string,
  code: string,
): Promise<BreakpointLines> {
  return api.post<BreakpointLines>(
    `/workflows/${workflowId}/nodes/${encodeURIComponent(nodeId)}/debug/breakpoint-lines`,
    { code },
  );
}

export interface DebugVar {
  name: string;
  value: string;
}

/** A server → client frame. */
export type DebugEvent =
  | { type: "ready" }
  | { type: "stopped"; reason: string; line: number | null }
  | { type: "terminated"; ok: boolean; value: unknown; error: string | null }
  | { type: "vars"; vars: DebugVar[] }
  | { type: "eval"; result: string; expression: string }
  | { type: "error"; error: string };

export interface DebugHandlers {
  onEvent: (ev: DebugEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

type WsCtor = new (url: string) => WebSocket;

/** `WebSocket.OPEN` — the numeric literal (1), so a mocked ctor without the static works. */
const WS_OPEN = 1;

/** Build the authed debug WS URL from the current origin (ws/wss mirrors http/https). */
export function codeNodeDebugSocketUrl(
  workflowId: string,
  nodeId: string,
  token: string,
  origin: { protocol: string; host: string } = window.location,
): string {
  const proto = origin.protocol === "https:" ? "wss:" : "ws:";
  const path = `/api/admin/workflows/${workflowId}/nodes/${encodeURIComponent(nodeId)}/debug`;
  return `${proto}//${origin.host}${path}?token=${encodeURIComponent(token)}`;
}

/**
 * A live debug session over one WebSocket. Construct, `connect()`, then drive it. On
 * open it sends the INIT frame `{code, input}` (the editor buffer + a sample item's
 * data) — the server builds a pure-JS `DebugSession`, starts it, and replies `ready`.
 */
export class CodeNodeDebugSession {
  private ws: WebSocket | null = null;

  constructor(
    private readonly url: string,
    private readonly code: string,
    private readonly input: unknown,
    private readonly handlers: DebugHandlers,
    private readonly WsImpl: WsCtor = WebSocket,
  ) {}

  connect(): void {
    const ws = new this.WsImpl(this.url);
    this.ws = ws;
    ws.onopen = () => {
      // Send the buffer + sample input as the FIRST frame; the server needs it to build
      // the session before it can reply `ready`.
      this.send({ code: this.code, input: this.input });
      this.handlers.onOpen?.();
    };
    ws.onclose = () => this.handlers.onClose?.();
    ws.onmessage = (ev: MessageEvent) => {
      let parsed: DebugEvent;
      try {
        parsed = JSON.parse(String(ev.data));
      } catch {
        return; // ignore a malformed frame rather than crash the session
      }
      this.handlers.onEvent(parsed);
    };
  }

  private send(msg: Record<string, unknown>): void {
    if (this.ws && this.ws.readyState === WS_OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  setBreakpoints(lines: number[]): void {
    this.send({ cmd: "setBreakpoints", lines });
  }

  resume(kind: StepKind = "continue"): void {
    this.send({ cmd: kind });
  }

  requestVars(frameId = 0): void {
    this.send({ cmd: "vars", frameId });
  }

  evaluate(expression: string, frameId = 0): void {
    this.send({ cmd: "evaluate", expression, frameId });
  }

  stop(): void {
    this.send({ cmd: "stop" });
  }

  close(): void {
    try {
      this.ws?.close();
    } catch {
      /* already closed */
    }
    this.ws = null;
  }
}
